import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import { type UpsertTariffRequest, UpsertTariffRequestSchema } from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { translateApiError } from '../../../../shared/errors/translateApiError';

// ── Types ──────────────────────────────────────────────────────────────────

/**
 * Values flowing through react-hook-form are in DOLLARS (display units).
 * Conversion to integer cents happens at submit time via setValueAs and the
 * dollar→cent transform in the parent onSubmit prop. The form never stores or
 * exposes cents to the UI; the parent never exposes dollars to the API.
 *
 * JQ4-17: 'nightly' mode is available for Hotel-niche locations.
 * Full nightly UI (guest/non-guest rate fields) added in WU-6.
 */
export interface TariffFormValues {
  mode: 'free' | 'flat' | 'hourly' | 'nightly';
  differentiatedByDay: boolean;
  /** Display value in dollars (string); converted to cents on submit. */
  amountCents: number | null;
  dailyCapCents: number | null;
  weekdayAmountCents: number | null;
  weekendAmountCents: number | null;
  /** Nightly mode — Hotel only (JQ4-17 · US-CONFIG-003). Null for other modes. */
  guestAmountCents: number | null;
  nonGuestAmountCents: number | null;
  /**
   * Delay-recharge grace period, in whole minutes (post-payment-delay-recharge
   * · S2). Applies to Flat/Hourly/Nightly, NOT Free. Already an integer — no
   * dollars<->cents conversion needed (unlike the monetary fields above).
   * null = system default (15 min), resolved server-side at compute time.
   */
  graceMinutes: number | null;
}

interface TariffFormProps {
  /**
   * Called with a fully-validated UpsertTariffRequest whose monetary fields
   * are INTEGER CENTS. The parent is responsible for calling the API.
   */
  onSubmit: (values: UpsertTariffRequest) => Promise<void>;
  /** Pre-populated defaults (monetary values already converted to dollars for display). */
  defaultValues?: Partial<TariffFormValues>;
  submitLabel?: string;
}

// ── Dollar ↔ Cent helpers ──────────────────────────────────────────────────

/** Display string → integer cents (used in setValueAs). */
function dollarsToCents(v: string | number | null | undefined): number | null {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100);
}

/** Display string → integer minutes (used in setValueAs for graceMinutes). */
function toIntMinutes(v: string | number | null | undefined): number | null {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return Math.round(n);
}

// ── Component ──────────────────────────────────────────────────────────────

export function TariffForm({ onSubmit, defaultValues, submitLabel }: TariffFormProps) {
  const { t } = useTranslation('location-tariffs');
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<TariffFormValues>({
    resolver: zodResolver(UpsertTariffRequestSchema),
    defaultValues: {
      mode: 'flat',
      differentiatedByDay: false,
      amountCents: null,
      dailyCapCents: null,
      weekdayAmountCents: null,
      weekendAmountCents: null,
      guestAmountCents: null,
      nonGuestAmountCents: null,
      graceMinutes: null,
      ...defaultValues,
    },
  });

  const mode = watch('mode');
  const differentiated = watch('differentiatedByDay');

  async function handleFormSubmit(values: TariffFormValues) {
    setServerError(null);
    try {
      // Build the API body (cents are already stored as numbers via setValueAs).
      // We cast through unknown because TariffFormValues and UpsertTariffRequest
      // are structurally equivalent after zodResolver transformation.
      await onSubmit(values as unknown as UpsertTariffRequest);
    } catch (e) {
      setServerError(translateApiError(e, t, { fallback: t('errors.generic') }));
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {serverError && <FormBanner message={serverError} />}

      {/* Mode selector */}
      <Field label={t('form.modeLabel')} htmlFor="mode" error={errors.mode?.message}>
        <select
          id="mode"
          disabled={isSubmitting}
          className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-[13px] shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
          {...register('mode')}
        >
          <option value="free">{t('form.modeOptions.free')}</option>
          <option value="flat">{t('form.modeOptions.flat')}</option>
          <option value="hourly">{t('form.modeOptions.hourly')}</option>
          <option value="nightly">{t('form.modeOptions.nightly')}</option>
        </select>
      </Field>

      {/* Free mode hint */}
      {mode === 'free' && <p className="text-[13px] text-jq-graphite">{t('form.freeHint')}</p>}

      {/* Nightly mode — guest rate */}
      {mode === 'nightly' && (
        <>
          <Field
            label={t('form.guestRate.label')}
            htmlFor="guestAmountCents"
            error={errors.guestAmountCents?.message}
          >
            <Input
              id="guestAmountCents"
              type="number"
              min={0}
              step="0.01"
              placeholder={t('form.amountPlaceholder')}
              disabled={isSubmitting}
              {...register('guestAmountCents', {
                setValueAs: dollarsToCents,
              })}
            />
          </Field>
          <Field
            label={t('form.nonGuestRate.label')}
            htmlFor="nonGuestAmountCents"
            error={errors.nonGuestAmountCents?.message}
          >
            <Input
              id="nonGuestAmountCents"
              type="number"
              min={0}
              step="0.01"
              placeholder={t('form.amountPlaceholder')}
              disabled={isSubmitting}
              {...register('nonGuestAmountCents', {
                setValueAs: dollarsToCents,
              })}
            />
          </Field>
        </>
      )}

      {/* Differentiation toggle — hidden when free or nightly */}
      {mode !== 'free' && mode !== 'nightly' && (
        <Field
          label={t('form.differentiate.label')}
          htmlFor="differentiatedByDay"
          error={errors.differentiatedByDay?.message}
        >
          <div className="flex items-center gap-2">
            <input
              id="differentiatedByDay"
              type="checkbox"
              disabled={isSubmitting}
              className="h-4 w-4"
              {...register('differentiatedByDay')}
            />
            <span className="text-[13px] text-jq-graphite">{t('form.differentiate.hint')}</span>
          </div>
        </Field>
      )}

      {/* Single amount field (non-differentiated, not free, not nightly) */}
      {mode !== 'free' && mode !== 'nightly' && !differentiated && (
        <Field
          label={mode === 'flat' ? t('form.flatFee.label') : t('form.ratePerHour.label')}
          htmlFor="amountCents"
          error={errors.amountCents?.message}
        >
          <Input
            id="amountCents"
            type="number"
            min={0}
            step="0.01"
            placeholder={t('form.amountPlaceholder')}
            disabled={isSubmitting}
            {...register('amountCents', {
              setValueAs: dollarsToCents,
            })}
          />
        </Field>
      )}

      {/* Differentiated amount fields */}
      {mode !== 'free' && mode !== 'nightly' && differentiated && (
        <>
          <Field
            label={t('form.weekdayAmount.label')}
            htmlFor="weekdayAmountCents"
            error={errors.weekdayAmountCents?.message}
          >
            <Input
              id="weekdayAmountCents"
              type="number"
              min={0}
              step="0.01"
              placeholder={t('form.amountPlaceholder')}
              disabled={isSubmitting}
              {...register('weekdayAmountCents', {
                setValueAs: dollarsToCents,
              })}
            />
          </Field>

          <Field
            label={t('form.weekendAmount.label')}
            htmlFor="weekendAmountCents"
            error={errors.weekendAmountCents?.message}
          >
            <Input
              id="weekendAmountCents"
              type="number"
              min={0}
              step="0.01"
              placeholder={t('form.amountPlaceholder')}
              disabled={isSubmitting}
              {...register('weekendAmountCents', {
                setValueAs: dollarsToCents,
              })}
            />
          </Field>
        </>
      )}

      {/* Daily cap — hourly only */}
      {mode === 'hourly' && (
        <Field
          label={t('form.dailyCap.label')}
          htmlFor="dailyCapCents"
          error={errors.dailyCapCents?.message}
        >
          <Input
            id="dailyCapCents"
            type="number"
            min={0}
            step="0.01"
            placeholder={t('form.dailyCap.placeholder')}
            disabled={isSubmitting}
            {...register('dailyCapCents', {
              setValueAs: dollarsToCents,
            })}
          />
          <p className="mt-1 text-[12px] text-jq-graphite">{t('form.dailyCap.hint')}</p>
        </Field>
      )}

      {/* Grace period — Flat/Hourly/Nightly only (post-payment-delay-recharge · S2) */}
      {mode !== 'free' && (
        <Field
          label={t('form.gracePeriod.label')}
          htmlFor="graceMinutes"
          error={errors.graceMinutes?.message}
        >
          <Input
            id="graceMinutes"
            type="number"
            min={0}
            step="1"
            placeholder={t('form.gracePeriod.placeholder')}
            disabled={isSubmitting}
            {...register('graceMinutes', {
              setValueAs: toIntMinutes,
            })}
          />
          <p className="mt-1 text-[12px] text-jq-graphite">{t('form.gracePeriod.hint')}</p>
        </Field>
      )}

      <div className="flex items-center justify-end gap-3 border-t border-jq-sand/70 pt-6">
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? t('form.saving') : (submitLabel ?? t('form.saveTariff'))}
        </Button>
      </div>
    </form>
  );
}

// ── Building blocks ────────────────────────────────────────────────────────

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}
